/**
 * Fase 6.5C — canonical browser API client.
 *
 * Security contract:
 *   1. All browser API calls use `withCredentials: true` so the HttpOnly
 *      access/refresh cookies AND the readable CSRF cookie ride along.
 *   2. The access JWT is NEVER stored in localStorage or sessionStorage.
 *      The AuthContext hydrates via `/auth/me` and never sees the token.
 *   3. Mutating requests (POST/PUT/PATCH/DELETE) automatically echo the
 *      readable `csrf_token` cookie into the `X-CSRF-Token` header, which
 *      the backend cross-checks against the cookie value.
 *   4. On 401 we attempt exactly one silent refresh, retry the original
 *      request, and give up (401 propagates) if the refresh also fails.
 *      This prevents refresh loops.
 *   5. On 403 with `detail.code === "csrf_invalid"` we surface a friendly
 *      i18n toast (NL/EN) so the user knows to refresh — never a raw
 *      stack trace.
 *
 * NOTE on XSS: HttpOnly cookies stop an XSS payload from ex-filtrating
 * the auth tokens via `document.cookie`. They do NOT prevent an active
 * XSS from making authenticated requests inside the victim's own tab.
 * CSRF protection guards against cross-site forgeries — it is orthogonal
 * to XSS.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const MUTATING = new Set(["post", "put", "patch", "delete"]);

// Read the readable CSRF cookie set by the backend on session establishment.
// Returns null if the cookie is missing (e.g. anonymous request or fresh
// tab before login).
export function readCsrfCookie() {
  if (typeof document === "undefined") return null;
  const raw = document.cookie || "";
  for (const part of raw.split(";")) {
    const [k, ...rest] = part.trim().split("=");
    if (k === "csrf_token") return decodeURIComponent(rest.join("=") || "");
  }
  return null;
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();

  // Fase 6.5C — inject X-CSRF-Token on mutating requests. Skip endpoints
  // that establish a session (they never carry the cookie yet).
  if (MUTATING.has(method)) {
    const csrf = readCsrfCookie();
    if (csrf) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = csrf;
    }
  }

  // Owner "focus on one location" mode: inject location_id into GETs unless
  // caller already set it. Applies to endpoints that understand the param
  // (shifts, users, teams, dashboard); others ignore unknown query params.
  try {
    const scope = localStorage.getItem("rostr.active_location");
    if (scope && method === "get") {
      config.params = config.params || {};
      if (config.params.location_id === undefined && !config.params.__no_scope) {
        config.params.location_id = scope;
      }
      delete config.params.__no_scope;
    }
  } catch { /* ignore */ }
  return config;
});

// -----------------------------------------------------------------
// Response interceptors:
//   * 402 → paywall toast (existing behaviour, unchanged)
//   * 401 → single silent refresh + retry
//   * 403 csrf_invalid → i18n toast
// -----------------------------------------------------------------
let _paywallToastShownAt = 0;
let _csrfToastShownAt = 0;
let _refreshInFlight = null;

// Test-only: reset transient throttles/inflight refresh. Never called from
// production code paths.
export function __resetForTests() {
  _paywallToastShownAt = 0;
  _csrfToastShownAt = 0;
  _refreshInFlight = null;
}

async function _showCsrfToast() {
  const now = Date.now();
  if (now - _csrfToastShownAt < 4000) return;
  _csrfToastShownAt = now;
  try {
    const { toast } = await import("sonner");
    // We can't reach the i18n hook here — mirror the strings from
    // /app/frontend/src/i18n/dict.js (csrf_invalid_toast_*) inline.
    const nl = document.documentElement.lang === "nl";
    toast.error(nl
      ? "Je beveiligde sessie kon niet worden geverifieerd. Vernieuw de pagina en probeer opnieuw."
      : "Your secure session could not be verified. Refresh the page and try again."
    );
  } catch { /* ignore */ }
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config || {};
    const detail = error?.response?.data?.detail;
    const detailCode = detail && typeof detail === "object" ? detail.code : null;

    // 403 csrf_invalid — surface friendly translated toast.
    if (status === 403 && detailCode === "csrf_invalid") {
      _showCsrfToast();
      return Promise.reject(error);
    }

    // 402 paywall — existing behaviour.
    if (status === 402) {
      const now = Date.now();
      if (now - _paywallToastShownAt > 4000) {
        _paywallToastShownAt = now;
        try {
          const { toast } = await import("sonner");
          const msg =
            (typeof detail === "string" ? detail : detail?.message) ||
            "Je bedrijf heeft geen actief abonnement.";
          toast.error(msg);
        } catch { /* ignore */ }
      }
      return Promise.reject(error);
    }

    // Fase 6.5B — 428 Precondition Required with detail.code = "terms_acceptance_required"
    // means the backend legal gate is blocking normal traffic. Broadcast so
    // the AuthContext / router can flip to the forced acceptance screen.
    // The 428 response never modifies user state; the SPA just needs to
    // know it must render <LegalAcceptance />.
    if (status === 428 && detailCode === "terms_acceptance_required") {
      try {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("rostr:legal-required"));
        }
      } catch { /* ignore */ }
      return Promise.reject(error);
    }

    // 401 → try refresh exactly once, then retry the original request.
    // `_retried` guards against infinite loops if refresh also 401s.
    if (
      status === 401 &&
      !cfg._retried &&
      !String(cfg.url || "").includes("/auth/refresh") &&
      !String(cfg.url || "").includes("/auth/login")
    ) {
      cfg._retried = true;
      try {
        // Coalesce parallel 401s onto a single refresh call.
        if (!_refreshInFlight) {
          _refreshInFlight = api.post("/auth/refresh").finally(() => {
            _refreshInFlight = null;
          });
        }
        await _refreshInFlight;
        return api.request(cfg);
      } catch (_) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

export default api;
